import type { ReactNode } from 'react'
import type { VaultDocLinkedTestDTO } from '@deepguide-ai/types'
import { Link } from '@tanstack/react-router'
import { Badge } from '@deepguide-ai/ui/badge'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { hasVaultDocumentVersionChanges, VaultDocumentVersionNotice } from './VaultDocumentVersionNotice'

const STATUS_LABELS: Record<string, string> = {
  NOT_STARTED: 'Not started', IN_PROGRESS: 'In progress', PASSED: 'Passed',
  FAILED: 'Failed', INCONCLUSIVE: 'Inconclusive', SUBMITTED: 'Pending review',
  NEEDS_RETEST: 'Needs retest', APPROVED: 'Approved', NOT_APPLICABLE: 'N/A',
  NON_PERFORMING: 'Non-performing', ON_HOLD: 'On hold', BLOCKED: 'Blocked',
}

/** The same related-Test card for ordinary and Key Documents. */
export function DocumentRelatedTest({ test, workspaceId, showStatus = true, document, children }: {
  test: Pick<VaultDocLinkedTestDTO, 'statusId' | 'title' | 'taskCode'> & { status: string; owner?: { name: string | null } | null }
  workspaceId: string
  showStatus?: boolean
  document?: { id: string; versionId: string | null; revision: number | null; currentRevision?: number; currentHasChanges?: boolean; linkToCurrent?: boolean }
  children?: ReactNode
}) {
  const hasVersionChanges = !!document?.versionId && hasVaultDocumentVersionChanges(document.revision, document.currentRevision, document.currentHasChanges)
  return <div className={`space-y-2 rounded-md border p-3 ${hasVersionChanges ? 'border-status-orange/50 bg-status-orange-bg' : 'bg-card'}`}>
    <div className='flex items-start gap-1.5'>
      <Link className='min-w-0 flex-1 text-xs font-medium leading-relaxed hover:underline'
        to='/workspaces/$workspaceId/controls'
        params={{ workspaceId }}
        search={{ test: test.statusId }}>
        {test.taskCode ? `TEST-${test.taskCode} · ` : ''}{test.title}
      </Link>
    </div>
    <div className='flex flex-wrap items-center justify-between gap-2 text-xs'>
      {showStatus && (
        <Badge status={['APPROVED', 'PASSED'].includes(test.status) ? 'green' : test.status === 'SUBMITTED' ? 'blue' : ['FAILED', 'BLOCKED'].includes(test.status) ? 'red' : 'default'}>
          {STATUS_LABELS[test.status] ?? test.status}
        </Badge>
      )}
      {'owner' in test && (
        <span className='text-muted-foreground inline-flex items-center gap-1.5'>
          Owner:
          {test.owner && <MemberAvatar person={test.owner} size='xs' />}
          {test.owner?.name ?? 'Unassigned'}
        </span>
      )}
    </div>
    {document && !hasVersionChanges && (
      document.versionId && document.revision === null ? (
        <span className='text-muted-foreground text-xs'>Linked version unavailable</span>
      ) : (
        <Link className='text-muted-foreground block text-xs hover:underline'
          to='/workspaces/$workspaceId/documents/$docId'
          params={{ workspaceId, docId: document.id }}
          search={{ versionId: document.versionId ?? undefined }}>
          {document.versionId ? `Submitted document · v${document.revision}` : `Current document${document.currentRevision ? ` · v${document.currentRevision}` : ''}`}
        </Link>
      )
    )}
    {document && hasVersionChanges && <VaultDocumentVersionNotice
      submittedVersionId={document.versionId}
      submittedRevision={document.revision} currentRevision={document.currentRevision}
      currentHasChanges={document.currentHasChanges} workspaceId={workspaceId} documentId={document.id}
      linkToCurrent={document.linkToCurrent} statusId={test.statusId} />}
    {children}
  </div>
}
